import { existsSync, readdirSync, readFileSync, statSync } from 'fs';
import { basename, join } from 'path';
import { parseYaml } from './yaml.js';
import { DATABASE_META_FILE, readMeta } from './workspace.js';
import {
  ENTITY_DIRS,
  type CollectionConfig,
  type DatabaseConfig,
  type DatabaseMeta,
  type ToolsetConfig,
  type EntityKind,
  type InboundWebhookConfig,
  type RelationshipTypeConfig,
  type ToolConfig,
  type TriggerConfig,
} from './types.js';

export interface ParsedDatabase {
  meta: DatabaseMeta;
  config: DatabaseConfig;
}

// Returns the parsed entity objects as `unknown[]` so callers can assert the concrete per-kind
// type with a single (type-checker-accepted) cast rather than a banned `as unknown as` double-cast.
function readEntityDir(dir: string): unknown[] {
  if (!existsSync(dir) || !statSync(dir).isDirectory()) return [];
  const out: Record<string, unknown>[] = [];
  for (const f of readdirSync(dir).sort()) {
    if (!f.endsWith('.yaml')) continue;
    const path = join(dir, f);
    let parsed: unknown;
    try {
      parsed = parseYaml(readFileSync(path, 'utf-8'));
    } catch (err) {
      throw new Error(`Failed to parse ${path}: ${(err as Error).message}`);
    }
    if (!parsed || typeof parsed !== 'object') {
      throw new Error(`${path} is empty or not a YAML object`);
    }
    const entity = parsed as Record<string, unknown>;
    // The id field is authoritative; warn if the filename drifted from it.
    if (entity.id === undefined) entity.id = basename(f, '.yaml');
    else if (String(entity.id) !== basename(f, '.yaml')) {
      throw new Error(`${path}: id '${String(entity.id)}' does not match filename '${basename(f, '.yaml')}.yaml'`);
    }
    out.push(entity);
  }
  return out;
}

/**
 * Read a database folder into `{ meta, config }`. Each absent entity subdir yields an empty
 * array — the config is the FULL desired state, so an omitted entity reads as a removal in the
 * diff (only actioned by `push --prune`).
 */
export function parseDatabaseFolder(folder: string): ParsedDatabase {
  const meta = readMeta(folder);
  if (!meta) {
    throw new Error(`No ${DATABASE_META_FILE} found in ${folder}`);
  }

  // Each subdir holds untyped YAML objects; cast per kind at the parse boundary (a single,
  // type-checker-accepted assertion since each typed entity is assignable to Record<string, unknown>).
  const read = (kind: EntityKind) => readEntityDir(join(folder, ENTITY_DIRS[kind]));
  const config: DatabaseConfig = {
    collections: read('collections') as CollectionConfig[],
    relationship_types: read('relationship_types') as RelationshipTypeConfig[],
    inbound_webhooks: read('inbound_webhooks') as InboundWebhookConfig[],
    triggers: read('triggers') as TriggerConfig[],
    toolsets: read('toolsets') as ToolsetConfig[],
    tools: read('tools') as ToolConfig[],
  };
  return { meta, config };
}
